import { apiClient } from './client';
import type { Department, DepartmentWrite } from '@/types/oscar';

/**
 * Typed wrappers over PR-26's `/api/onboarding/departments*` endpoints.
 * Reads are open to any authenticated Oscar user; mutations are admin-only
 * (enforced server-side — these just surface the calls).
 */

/** GET /api/onboarding/departments — list (active by default; all when includeDeleted). */
export function listDepartments(includeDeleted = false, signal?: AbortSignal): Promise<Department[]> {
  const qs = includeDeleted ? '?includeDeleted=true' : '';
  return apiClient.get<Department[]>(`/api/onboarding/departments${qs}`, signal);
}

/** GET /api/onboarding/departments/:code — one department. */
export function getDepartment(code: string, signal?: AbortSignal): Promise<Department> {
  return apiClient.get<Department>(`/api/onboarding/departments/${encodeURIComponent(code)}`, signal);
}

/** POST /api/onboarding/departments — create (admin). */
export function createDepartment(body: DepartmentWrite): Promise<Department> {
  return apiClient.post<Department>('/api/onboarding/departments', body);
}

/** PATCH /api/onboarding/departments/:code — update description/geo/BU (admin). */
export function updateDepartment(code: string, body: DepartmentWrite): Promise<Department> {
  return apiClient.patch<Department>(`/api/onboarding/departments/${encodeURIComponent(code)}`, body);
}

/** POST /api/onboarding/departments/:code/retire — soft delete (admin). 409 if titles reference it. */
export function retireDepartment(code: string): Promise<Department> {
  return apiClient.post<Department>(`/api/onboarding/departments/${encodeURIComponent(code)}/retire`);
}

/** POST /api/onboarding/departments/:code/restore — un-retire (admin). */
export function restoreDepartment(code: string): Promise<Department> {
  return apiClient.post<Department>(`/api/onboarding/departments/${encodeURIComponent(code)}/restore`);
}
